/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {Component, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {
    AchievementClaimResponseInterface,
    CommunityChallengeAchievementInterface,
    CommunityChallengeInterface,
    CommunityChallengeMilestoneInterface,
    CommunityChallengeRewardInterface,
    GameAchievementAnnounceInterface,
} from '@/store/modules/community-challenge/types';
import {AudioApiCallInterface, GameApiCallInterface, SpeciesInterface} from '@/store/modules/game/types';
import OverlayDisable from '@/components/OverlayDisable/index.vue';
import {PersonalMilestoneProgress} from '@/components/CommunityChallengeContribution/types';
import Popper from 'vue-popperjs';

/**
 * Community Challenge Contribution component.
 * @author Mark Thompson <mthompson@frontier.co.uk>
 */
@Component({
    name: 'c-community-challenge-contribution',
    components: {
        'c-overlay-disable': OverlayDisable,
        'popper': Popper,
    },
})
export default class CommunityChallengeContribution extends Vue {
    @Getter('challenge', {namespace: 'communityChallengeModule'})
    public challenge!: CommunityChallengeInterface;

    @Getter('count', {namespace: 'communityChallengeModule'})
    public count!: number;

    @Getter('unclaimed', {namespace: 'communityChallengeModule'})
    public unclaimed!: number;

    @Getter('claimed', {namespace: 'communityChallengeModule'})
    public claimed!: number;

    @Getter('isClaimable', {namespace: 'communityChallengeModule'})
    public isClaimable!: boolean;

    @Getter('claimable', {namespace: 'communityChallengeModule'})
    public claimable!: CommunityChallengeAchievementInterface[];

    @Getter('achievements', {namespace: 'communityChallengeModule'})
    public achievements!: CommunityChallengeAchievementInterface[];

    @Getter('personalMilestones', {namespace: 'communityChallengeModule'})
    public milestones!: CommunityChallengeMilestoneInterface[];

    @Getter('getSpeciesById', {namespace: 'gameModule'})
    public getSpeciesById!: (id: string) => SpeciesInterface;

    @Action('claim', {namespace: 'communityChallengeModule'})
    public claim!: (achievement: CommunityChallengeAchievementInterface) =>
        Promise<AchievementClaimResponseInterface>;

    @Action('callGameApi', {namespace: 'gameModule'})
    public callGameApi!: (gameApiCall: GameApiCallInterface) => void;

    @Action('callAudioApi', {namespace: 'gameModule'})
    public callAudioApi!: (audioApiCall: AudioApiCallInterface) => void;

    public claiming: boolean = false;

    public readonly ITEM_COUNT: number = 11;

    /**
     * Retrieves the segments used in graph.
     * @return {any[]}
     */
    public get segments(): any[] {
        if (undefined === this.count) {
            return [];
        }


        const contributions: PersonalMilestoneProgress[] = [];
        const pivot: number = Math.floor(this.ITEM_COUNT / 2);

        // generate pages
        for (let i: number = this.count - pivot; i <= this.count + pivot; i++) {
            const contribution: PersonalMilestoneProgress = {
                count: i,
                locked: false,
                unclaimed: false,
                rewards: [],
            };

            const milestone: CommunityChallengeMilestoneInterface|undefined = this.milestones.find(
                (item: CommunityChallengeMilestoneInterface) => item.quantity === i);

            if (milestone !== undefined && milestone.rewards.length > 0) {
                contribution.rewards = milestone.rewards;
                contribution.locked = this.count < i;
                contribution.unclaimed = i > 0 && this.count >= i;
                if (contribution.unclaimed) {
                    const achievement: CommunityChallengeAchievementInterface|undefined = this.achievements.find(
                        (item: CommunityChallengeAchievementInterface) => {
                            return item['@id'].replace(/^.*milestone=(\d+).*$/, '$1') === `${milestone.id}`;
                        });

                    contribution.unclaimed = contribution.unclaimed &&
                        !(undefined !== achievement && !achievement.claimable);
                }

            }

            contributions.push(contribution);
        }


        return contributions;
    }

    /**
     * @param {CommunityChallengeRewardInterface[]} rewards
     * @return {any}
     */
    public getRewardsInfoText(rewards: CommunityChallengeRewardInterface[]): string {
        return rewards.reduce((reducer: string[], reward: CommunityChallengeRewardInterface) => {
            switch (reward['@type']) {
                case 'RewardCredit':
                    reducer.push(`${reward.amount}CC`);
                    break;
                case 'RewardAnimal':
                case 'RewardAnimalAlbino':
                    reducer.push((reward.amount || 1) + ' x ' +
                        this.$i18n.translate('achievements.cc.achievements.animal', '') || '');
                    break;
                case 'RewardAvatarShorts':
                    reducer.push(this.$i18n.translate('achievements.cc.achievements.pants', '') || '');
                    break;
                case 'RewardAvatarHat':
                    reducer.push(this.$i18n.translate('achievements.cc.achievements.hat', '') || '');
                    break;
                case 'RewardAvatarShirt':
                    reducer.push(this.$i18n.translate('achievements.cc.achievements.t-shirt', '') || '');
                    break;
            }
            return reducer;
        }, []).join('<br>');
    }

    /**
     * Reward icon loader.
     * @param {CommunityChallengeRewardInterface} reward
     * @return {any}
     */
    public getRewardIcon(reward: CommunityChallengeRewardInterface): any {
        try {
            switch (reward['@type']) {
                case 'RewardCredit':
                    return require(`@/assets/icons/price_credits.svg`);
                case 'RewardAnimal':
                case 'RewardAnimalAlbino':
                    return require(`@/assets/icons/animal.svg`);
                case 'RewardAvatarShorts':
                    return require(`@/assets/icons/cosmetic_pants.svg`);
                case 'RewardAvatarHat':
                    return require(`@/assets/icons/cosmetic_hat.svg`);
                case 'RewardAvatarShirt':
                    return require(`@/assets/icons/cosmetic_top.svg`);
            }
        } catch (e) {
            // do nothing
        }
    }

    /**
     * Get medal class of reward.
     * @param {CommunityChallengeRewardInterface} reward
     * @return {string}
     */
    public getRewardClass(reward: CommunityChallengeRewardInterface): string {
        if ('RewardCredit' === reward['@type'] && !!reward.amount) {
            if (reward.amount > 125) {
                return 'gold';
            }
            if (reward.amount > 50) {
                return 'silver';
            }
        }

        if ('RewardAvatar' === reward['@type'].substr(0, 12)) {
            return 'gold';
        }

        return 'plain';
    }

    /**
     * Claim the rewards one at a time.
     * @return {Promise<void>}
     */
    public async claimRewards(): Promise<void> {
        this.callAudioApi({type: 'press'});
        this.claiming = true;
        // apply claim one at a time

        let cc: number = 0;
        let animals: string[] = [];
        let inventory: string[] = [];

        try {
            for (const achievement of this.claimable) {
                if (!achievement.claimable) {
                    continue;
                }

                const claim: AchievementClaimResponseInterface = await this.claim(achievement);
                if (!!claim.cc && claim.cc > 0) {
                    cc += claim.cc;
                }

                if (!!claim.animals && claim.animals.length > 0) {
                    animals = animals.concat(claim.animals);
                }

                if (!!claim.inventory && claim.inventory.length > 0) {
                    inventory = inventory.concat(claim.inventory);
                }
            }

            const params: GameAchievementAnnounceInterface = {};
            if (cc > 0) {
                params.cc = cc;
            }

            if (inventory.length > 0) {
                params.inventory = inventory.join(':');
            }

            if (animals.length > 0) {
                params.animal = animals.join(':');
            }

            this.callGameApi({route: 'award-claim', params});
            this.claiming = false;
        } catch (e) {
            this.claiming = false;
        }
    }
}
